import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BadgePercent, Check, AlertTriangle, Info, Euro, Percent, ClipboardCheck, FileText, ChevronDown, ExternalLink } from "lucide-react";
import { Reveal, useSeo } from "../lib/motion";
import { CTASection } from "../components/CTASection";

/* ---------- Petits blocs réutilisables (identité du site) ---------- */

function Section({ id, tint = "white", children, testid }) {
  return (
    <section id={id} data-testid={testid} className={tint === "cloud" ? "bg-cloud" : "bg-white"}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">{children}</div>
    </section>
  );
}

function SectionHead({ icon: Icon, kicker, title, sub }) {
  return (
    <Reveal className="mb-8 max-w-3xl">
      <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-ice"><Icon size={15} /> {kicker}</span>
      <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl text-ink mt-4 leading-[1.05]">{title}</h2>
      {sub && <p className="text-maintext text-lg mt-4 leading-relaxed">{sub}</p>}
    </Reveal>
  );
}

function BlockTitle({ children }) {
  return <h3 className="font-heading font-bold text-lg md:text-xl text-ink mb-4">{children}</h3>;
}

function CheckList({ items }) {
  return (
    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed">
          <Check size={17} className="text-ice mt-0.5 shrink-0" /> {it}
        </li>
      ))}
    </ul>
  );
}

function Callout({ kind = "info", title, children }) {
  const styles = {
    info: "border-ice bg-ice/5",
    warn: "border-amber-500 bg-amber-50",
    note: "border-ink bg-cloud",
    plain: "border-ink bg-white",
  };
  const IconEl = kind === "warn" ? AlertTriangle : Info;
  return (
    <div className={`border-l-4 rounded-r-md p-5 ${styles[kind]}`}>
      <p className="flex items-center gap-2 font-heading font-bold text-ink text-sm uppercase tracking-widest"><IconEl size={16} className={kind === "warn" ? "text-amber-600" : "text-ice"} /> {title}</p>
      <div className="text-maintext text-[15px] mt-2.5 leading-relaxed">{children}</div>
    </div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-line rounded-md p-6 md:p-7 ${className}`}>
      {title && <BlockTitle>{title}</BlockTitle>}
      {children}
    </div>
  );
}

function CtaLink({ to, testid, children }) {
  return (
    <Link to={to} data-testid={testid} className="inline-flex items-center gap-2 bg-ice text-white rounded-md px-7 py-3.5 font-bold mt-8 hover:bg-icehover transition-transform hover:scale-105">
      {children} <ArrowUpRight size={17} />
    </Link>
  );
}

/* ---------- Contenu ---------- */

const CEE_CONDITIONS = [
  "Bâtiment résidentiel existant",
  "Installation réalisée par un professionnel",
  "PAC air/air éligible à la fiche CEE BAR-TH-129",
  "Puissance nominale inférieure ou égale à 12 kW",
  "SCOP supérieur ou égal à 3,9",
  "Caractéristiques techniques du matériel justifiables par la documentation fabricant",
  "Marque et référence du matériel clairement identifiables",
  "Dossier conforme aux exigences de l’opérateur CEE",
];

const TVA10_BENEFICIAIRES = [
  "Propriétaire occupant",
  "Propriétaire bailleur",
  "Locataire",
  "Occupant à titre gratuit",
  "Résidence principale ou secondaire",
  "Logement achevé depuis plus de 2 ans",
];

const TVA10_VERIFICATIONS = [
  "Ancienneté du logement",
  "Nature exacte de la prestation",
  "Fourniture ou pose",
  "Remplacement ou entretien",
  "Ampleur des travaux",
  "Absence de création d’un immeuble fiscalement neuf",
  "Conditions déclarées par le client sur devis ou facture",
];

const TVA55_EQUIPEMENTS = [
  "PAC air/air réversible",
  "Équipement fixe",
  "Installation permanente dans le bâtiment",
  "Cycle à compression entraîné par un moteur électrique",
];

const TVA55_AUTRES = [
  "Respect des exigences réglementaires applicables au fluide frigorigène au moment de la signature du devis",
  "Équipement compatible avec les règles européennes F-Gas en vigueur",
  "Possibilité de connexion à un réseau numérique",
  "Possibilité de recevoir et transmettre des consignes de température",
  "Programmation horaire",
  "Pilotage du fonctionnement en chauffage et en refroidissement",
];

const TVA55_VERIFIE = [
  "Puissance nominale",
  "Classe énergétique",
  "SCOP / SEER",
  "Références fabricant",
  "Fluide utilisé",
  "Conformité réglementaire",
  "Compatibilité avec les critères fiscaux en vigueur",
];

const COMPARATIF = [
  { d: "CEE", p: "PAC air/air éligible", l: "Bâtiment existant", c: "Oui", m: "Prime variable", v: "Selon opérateur CEE" },
  { d: "TVA 10 %", p: "Certains travaux / entretien", l: "Oui", c: "Selon la nature des travaux", m: "10 %", v: "HP Froid + conditions fiscales" },
  { d: "TVA 5,5 %", p: "Certaines PAC air/air éligibles", l: "Oui, selon les conditions applicables", c: "Oui, critères techniques stricts", m: "5,5 %", v: "Vérification du matériel et du chantier" },
];

const CAS = [
  { t: "Installation d’un mono-split performant dans une maison de plus de 2 ans", x: ["TVA 5,5 % potentiellement applicable si l’équipement remplit tous les critères.", "CEE potentiellement applicable si la PAC respecte notamment la puissance et le SCOP requis."] },
  { t: "Installation d’un multi-split dans un logement ancien", x: ["TVA 5,5 % potentiellement applicable si le multi-split atteint au minimum les classes énergétiques requises.", "CEE à vérifier selon la puissance et le SCOP."] },
  { t: "Entretien d’une climatisation existante", x: ["Pas de CEE lié à une nouvelle installation.", "TVA à vérifier selon la nature exacte de la prestation et les conditions fiscales."] },
  { t: "Matériel acheté directement par le client", x: ["L’achat direct du matériel peut relever du taux normal.", "Le taux appliqué à la pose doit être étudié séparément."] },
];

const DOCUMENTS = [
  "Adresse ou commune du chantier",
  "Type de logement",
  "Année approximative de construction",
  "Type d’équipement souhaité",
  "Nombre d’unités",
  "Marque / référence si déjà choisie",
  "Devis ou documentation matériel si disponible",
  "Photos de l’installation existante",
  "Informations sur le projet",
];

const PROCESSUS = [
  "Qualification du projet",
  "Vérification de l’ancienneté et de la nature du logement",
  "Vérification des caractéristiques techniques du matériel",
  "Identification du taux de TVA applicable",
  "Vérification éventuelle de l’éligibilité CEE",
  "Établissement du devis avec les éléments applicables",
];

const FAQ = [
  { q: "Est-ce que toutes les climatisations bénéficient de la TVA à 5,5 % ?", r: "Non. L’équipement et le chantier doivent respecter les critères réglementaires en vigueur." },
  { q: "Une PAC air/air peut-elle bénéficier des CEE ?", r: "Oui, sous conditions, notamment techniques (puissance, SCOP, fiche d’opération standardisée)." },
  { q: "Le montant de la prime CEE est-il garanti ?", r: "Non. Il dépend notamment de l’opérateur et des conditions du programme en vigueur au moment de la demande." },
  { q: "Puis-je acheter moi-même le matériel ?", r: "Oui, mais cela peut modifier le traitement fiscal et l’application du taux réduit. Le taux applicable à la pose doit être étudié séparément." },
  { q: "Les aides sont-elles cumulables ?", r: "Certains dispositifs peuvent se cumuler, mais cela doit être vérifié au cas par cas." },
  { q: "Puis-je obtenir MaPrimeRénov’ pour une PAC air/air ?", r: "Nous ne pouvons pas le promettre pour une PAC air/air : les dispositifs évoluent. HP Froid vérifie les aides en vigueur avant toute mention sur le devis." },
  { q: "À quel moment vérifier les aides ?", r: "Avant l’engagement définitif des travaux, afin de ne pas perdre une éligibilité éventuelle." },
];

const SOURCES = [
  { label: "impots.gouv.fr — taux de TVA applicables aux travaux", url: "https://www.impots.gouv.fr" },
  { label: "bofip.impots.gouv.fr — Bulletin officiel des finances publiques", url: "https://bofip.impots.gouv.fr" },
  { label: "ecologie.gouv.fr — Certificats d’économies d’énergie (CEE)", url: "https://www.ecologie.gouv.fr/politiques-publiques/certificats-deconomies-denergie-cee" },
  { label: "Légifrance — article 278-0 bis A du CGI et arrêté du 13 juillet 2026", url: "https://www.legifrance.gouv.fr" },
  { label: "Fiche CEE BAR-TH-129 — fiches d’opérations standardisées", url: "https://www.ecologie.gouv.fr/fiches-doperations-standardisees" },
];

/* ---------- Page ---------- */

export default function Aides() {
  useSeo("Aides, CEE et TVA réduite", "Aide CEE, TVA à 10 % et TVA à 5,5 % : conditions, éligibilité et vérifications pour votre projet de climatisation ou pompe à chaleur air/air en Alsace.");
  const [open, setOpen] = useState(null);

  return (
    <div className="pt-28 bg-white">
      {/* 1. INTRODUCTION */}
      <section className="bg-cloud border-b border-line" data-testid="aides-intro">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <Reveal className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-ice"><BadgePercent size={15} /> Financement</span>
            <h1 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ink mt-4 leading-[1.03]">Aides, CEE et TVA réduite</h1>
            <p className="text-maintext text-lg mt-5 leading-relaxed">Selon la nature de votre projet, l’ancienneté du logement, le type d’équipement installé et ses performances, différentes aides ou taux réduits de TVA peuvent s’appliquer. HP Froid vérifie les conditions applicables à votre chantier avant l’établissement du devis.</p>
          </Reveal>
          <Reveal className="mt-8 max-w-3xl">
            <Callout kind="warn" title="Important">
              Les aides et taux réduits ne sont jamais automatiques. Ils dépendent du logement, des travaux réalisés, des caractéristiques du matériel et des règles en vigueur au moment du devis.
            </Callout>
          </Reveal>
        </div>
      </section>

      {/* 2. AIDE CEE */}
      <Section testid="section-cee">
        <SectionHead icon={Euro} kicker="Dispositif 01" title="Aide CEE – Certificats d’économies d’énergie" sub="Une prime peut être accordée pour certaines PAC air/air." />
        <Reveal className="max-w-3xl">
          <p className="text-maintext leading-relaxed">Les Certificats d’économies d’énergie sont un dispositif financé par les fournisseurs d’énergie et certains opérateurs. Ils permettent de verser une prime pour certaines opérations améliorant la performance énergétique d’un logement.</p>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <Reveal>
            <Card title="Principales conditions pour une PAC air/air" className="h-full">
              <CheckList items={CEE_CONDITIONS} />
            </Card>
          </Reveal>
          <div className="space-y-6">
            <Reveal delay={0.05}>
              <Callout kind="note" title="Montant de la prime">
                Le montant de la prime CEE n’est pas fixe. Il varie selon l’opérateur, l’équipement, le logement et les conditions du programme au moment de la demande.
              </Callout>
            </Reveal>
            <Reveal delay={0.1}>
              <Callout kind="info" title="À quel moment faire la demande ?">
                La démarche CEE doit être engagée au bon moment dans le processus. Il est important de vérifier les conditions avant l’engagement définitif des travaux afin de ne pas perdre l’éligibilité.
              </Callout>
            </Reveal>
          </div>
        </div>
        <Reveal className="mt-6 max-w-3xl">
          <p className="text-muted2 text-[15px] leading-relaxed">HP Froid peut identifier si le matériel envisagé répond aux critères techniques, mais le montant et l’accord définitif de la prime dépendent de l’organisme CEE.</p>
        </Reveal>
        <Reveal><CtaLink to="/demande-de-devis?aide=cee" testid="cta-cee">Vérifier mon éligibilité CEE</CtaLink></Reveal>
      </Section>

      {/* 3. TVA 10 % */}
      <Section tint="cloud" testid="section-tva10">
        <SectionHead icon={Percent} kicker="Dispositif 02" title="TVA à 10 % sur certains travaux" sub="Le taux de TVA à 10 % peut s’appliquer à certains travaux d’amélioration, de transformation, d’aménagement ou d’entretien réalisés dans un logement d’habitation achevé depuis plus de 2 ans." />
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <Card title="Qui peut en bénéficier ?" className="h-full">
              <CheckList items={TVA10_BENEFICIAIRES} />
            </Card>
          </Reveal>
          <Reveal delay={0.05}>
            <Card title="Exemples d’éléments à vérifier" className="h-full">
              <CheckList items={TVA10_VERIFICATIONS} />
            </Card>
          </Reveal>
        </div>
        <Reveal className="mt-6 max-w-3xl">
          <Callout kind="plain" title="Ce qu’il faut comprendre pour la climatisation">
            <p>La fourniture d’un système de climatisation ou d’une PAC air/air n’est pas automatiquement soumise à 10 %. Selon la nature de l’opération, certaines prestations de pose, d’installation ou d’entretien peuvent bénéficier d’un taux réduit lorsque les conditions fiscales sont remplies, alors que certains équipements peuvent relever d’un autre taux.</p>
          </Callout>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Reveal>
            <Callout kind="warn" title="Travaux exclus du taux réduit">
              Les travaux lourds assimilables à une construction neuve, ou rendant l’immeuble à l’état neuf, ne bénéficient pas des taux réduits.
            </Callout>
          </Reveal>
          <Reveal delay={0.05}>
            <Callout kind="info" title="À retenir — formalités">
              Depuis mars 2025, les anciennes attestations simplifiées ou normales ont été remplacées par une mention du client sur le devis ou la facture attestant que les conditions sont remplies.
            </Callout>
          </Reveal>
        </div>
        <Reveal><CtaLink to="/demande-de-devis?aide=tva10" testid="cta-tva10">Vérifier la TVA applicable à mon projet</CtaLink></Reveal>
      </Section>

      {/* 4. TVA 5,5 % */}
      <Section testid="section-tva55">
        <SectionHead icon={BadgePercent} kicker="Dispositif 03" title="TVA à 5,5 % pour certaines PAC air/air" sub="Depuis 2026, certaines pompes à chaleur air/air peuvent bénéficier du taux de TVA à 5,5 % lorsqu’elles répondent aux critères réglementaires de performance, de durabilité et d’installation." />
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <Card title="Équipements concernés" className="h-full">
              <CheckList items={TVA55_EQUIPEMENTS} />
            </Card>
          </Reveal>
          <Reveal delay={0.05}>
            <Card title="PAC ≤ 12 kW — classes énergétiques minimales" className="h-full">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-cloud border border-line rounded-md p-5">
                  <p className="font-heading font-bold text-ink">Mono-split</p>
                  <ul className="mt-3 space-y-2 text-maintext text-[15px]">
                    <li className="flex items-start gap-2"><Check size={16} className="text-ice mt-0.5 shrink-0" /> Classe énergétique au minimum A++ en chauffage</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-ice mt-0.5 shrink-0" /> Classe énergétique au minimum A++ en refroidissement</li>
                  </ul>
                </div>
                <div className="bg-cloud border border-line rounded-md p-5">
                  <p className="font-heading font-bold text-ink">Multi-split</p>
                  <ul className="mt-3 space-y-2 text-maintext text-[15px]">
                    <li className="flex items-start gap-2"><Check size={16} className="text-ice mt-0.5 shrink-0" /> Classe énergétique au minimum A+ en chauffage</li>
                    <li className="flex items-start gap-2"><Check size={16} className="text-ice mt-0.5 shrink-0" /> Classe énergétique au minimum A+ en refroidissement</li>
                  </ul>
                </div>
              </div>
            </Card>
          </Reveal>
        </div>
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Reveal>
            <Card title="Autres critères" className="h-full">
              <CheckList items={TVA55_AUTRES} />
            </Card>
          </Reveal>
          <div className="space-y-6">
            <Reveal delay={0.05}>
              <Callout kind="warn" title="PAC de puissance supérieure à 12 kW">
                Il existe des critères spécifiques d’efficacité énergétique saisonnière selon la catégorie d’équipement. L’éligibilité doit alors être vérifiée modèle par modèle, sans simplification.
              </Callout>
            </Reveal>
            <Reveal delay={0.1}>
              <Card title="Ce que HP Froid vérifie">
                <div className="flex flex-wrap gap-2">
                  {TVA55_VERIFIE.map((v) => <span key={v} className="text-xs font-semibold text-muted2 bg-cloud border border-line rounded px-2.5 py-1">{v}</span>)}
                </div>
              </Card>
            </Reveal>
          </div>
        </div>
        <Reveal><CtaLink to="/demande-de-devis?aide=tva55" testid="cta-tva55">Vérifier l’éligibilité de mon matériel</CtaLink></Reveal>
      </Section>

      {/* 5. COMPARATIF */}
      <Section tint="cloud" testid="aides-comparatif">
        <SectionHead icon={ClipboardCheck} kicker="Vue d’ensemble" title="Comparatif simple des dispositifs" />
        <Reveal>
          <div className="overflow-x-auto bg-white border border-line rounded-md">
            <table className="w-full min-w-[760px] text-left text-[15px]">
              <thead>
                <tr className="border-b border-line bg-cloud">
                  {["Dispositif", "Projet concerné", "Logement > 2 ans", "Critères matériel", "Montant / taux", "Validation"].map((h) => (
                    <th key={h} className="px-5 py-4 font-heading font-bold text-ink text-sm uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARATIF.map((r) => (
                  <tr key={r.d} className="border-b border-line last:border-0 hover:bg-ice/5 transition-colors">
                    <td className="px-5 py-4 font-heading font-bold text-ice whitespace-nowrap">{r.d}</td>
                    <td className="px-5 py-4 text-maintext">{r.p}</td>
                    <td className="px-5 py-4 text-maintext">{r.l}</td>
                    <td className="px-5 py-4 text-maintext">{r.c}</td>
                    <td className="px-5 py-4 font-semibold text-ink whitespace-nowrap">{r.m}</td>
                    <td className="px-5 py-4 text-maintext">{r.v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <Reveal className="mt-5 max-w-3xl">
          <p className="text-muted2 text-sm leading-relaxed">Tableau indicatif : l’éligibilité finale est toujours validée au moment du devis, selon les règles en vigueur.</p>
        </Reveal>
      </Section>

      {/* 6. CAS PRATIQUES */}
      <Section testid="aides-cas">
        <SectionHead icon={FileText} kicker="Pour y voir clair" title="Exemples concrets" />
        <div className="grid sm:grid-cols-2 gap-6">
          {CAS.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.06}>
              <div className="h-full bg-cloud border border-line rounded-md p-6 md:p-7">
                <span className="font-mono text-xs text-ice">Cas {i + 1}</span>
                <h3 className="font-heading font-bold text-lg text-ink mt-2 leading-snug">{c.t}</h3>
                <ul className="mt-4 space-y-2.5">
                  {c.x.map((l) => (
                    <li key={l} className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed">
                      <ArrowRight size={15} className="text-ice mt-1 shrink-0" /> {l}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 7. DOCUMENTS À FOURNIR */}
      <Section tint="cloud" testid="aides-documents">
        <SectionHead icon={FileText} kicker="Préparer votre demande" title="Pour vérifier votre éligibilité" sub="Ces informations peuvent nous être demandées pour étudier votre dossier." />
        <Reveal>
          <Card>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
              {DOCUMENTS.map((d) => (
                <li key={d} className="flex items-start gap-2.5 text-maintext text-[15px] leading-relaxed">
                  <Check size={17} className="text-ice mt-0.5 shrink-0" /> {d}
                </li>
              ))}
            </ul>
          </Card>
        </Reveal>
      </Section>

      {/* 8. PROCESSUS HP FROID */}
      <Section testid="aides-process">
        <SectionHead icon={ClipboardCheck} kicker="Notre méthode" title="Comment nous vérifions votre dossier" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROCESSUS.map((p, i) => (
            <Reveal key={p} delay={i * 0.06}>
              <div className="border-t-2 border-ice pt-5">
                <span className="font-heading font-black text-3xl text-ice/25 leading-none">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-heading font-bold text-ink mt-3 leading-snug">{p}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8 max-w-3xl">
          <Callout kind="info" title="À retenir">
            L’éligibilité finale est validée au moment de l’établissement du devis, selon les caractéristiques du chantier, du matériel et les règles en vigueur.
          </Callout>
        </Reveal>
      </Section>

      {/* 9. FAQ */}
      <Section tint="cloud" testid="aides-faq">
        <SectionHead icon={Info} kicker="Questions fréquentes" title="FAQ — Aides et TVA" />
        <div className="max-w-3xl space-y-3">
          {FAQ.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.04}>
              <div className="bg-white border border-line rounded-md overflow-hidden">
                <button type="button" data-testid={`faq-trigger-${i}`} onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 font-heading font-bold text-ink hover:text-ice transition-colors">
                  {f.q}
                  <ChevronDown size={18} className={`text-ice shrink-0 transition-transform duration-300 ${open === i ? "rotate-180" : ""}`} />
                </button>
                <div className={`grid transition-[grid-template-rows] duration-300 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-maintext text-[15px] leading-relaxed" data-testid={`faq-answer-${i}`}>{f.r}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 10. SOURCES OFFICIELLES */}
      <Section testid="aides-sources">
        <SectionHead icon={ExternalLink} kicker="Transparence" title="Sources officielles" />
        <Reveal>
          <Card>
            <ul className="space-y-3">
              {SOURCES.map((s) => (
                <li key={s.label}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-ice font-semibold text-[15px] hover:gap-3 transition-[gap]">
                    {s.label} <ExternalLink size={14} />
                  </a>
                </li>
              ))}
            </ul>
            <p className="text-muted2 text-sm mt-6 leading-relaxed border-t border-line pt-5">Informations vérifiées à partir des sources officielles. Les textes peuvent évoluer.</p>
          </Card>
        </Reveal>
      </Section>

      {/* 11. CTA FINAL */}
      <CTASection title="Un projet ? Nous vérifions les aides applicables." text="Décrivez votre installation : HP Froid identifie les taux de TVA et dispositifs qui peuvent s’appliquer à votre chantier, sous conditions." />
    </div>
  );
}
